import React, { useContext } from 'react';

import type {
  AgentName,
  AiModel,
  SwapAgentToolCall,
  ToolCallType,
  WorkflowAiResult,
  WorkflowAiToolCallResult,
} from '@aline/common';
import {
  assertNeverSafely,
  getExternalConnectDescription,
  getExternalConnectTitle,
  getExternalToolCallTitle,
  getExternalToolkitLabel,
  getExternalToolResolutionLabel,
  getIsAgentMessageToolCallProcessing,
  getPendingToolApprovalForToolCall,
  getToolApprovalForToolCall,
  getToolOutputForToolCall,
  toolCallSchema,
  WorkflowAiResultSchema,
  WorkflowAiToolCallResultSchema,
} from '@aline/common';
import {
  getSkillAuthoringPresentation,
  getSkillAuthoringToolOutput,
} from '@aline/common/agent-skills/skill-authoring-presentation';
import { getReportCreationPresentation } from '@aline/common/report-creation-presentation';
import { link } from '@aline/routes';
import { CircularProgress, Link as MuiLink, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  ArchiveRestore,
  BookOpenText,
  ChartColumnBig,
  CheckCircle,
  MessageCircle,
  Search,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { AskUserInlineSummary, getAskUserAnswers } from 'src/components/ai/chat/AskUser/AskUser';
import { MentionEntityContext } from 'src/components/ai/chat/chat-hooks/useMentionAccessContext';
import type { SkillVersionMentionEntity } from 'src/components/ai/chat/chat-hooks/useMentionAccessContext';
import { AlineTooltip, Spacer } from 'src/components/generic';
import type { SimpleIconInterface } from 'src/components/icons/icon-utils';
import { ToolCallCalculatorIcon } from 'src/components/icons/ToolCallCalculatorIcon';
import { ToolCallGetWebpagesIcon } from 'src/components/icons/ToolCallGetWebpagesIcon';
import { ToolCallModelFallbackIcon } from 'src/components/icons/ToolCallModelFallbackIcon';
import { ToolCallQueryDocumentIcon } from 'src/components/icons/ToolCallQueryDocumentIcon';
import { ToolCallQuestionMarkIcon } from 'src/components/icons/ToolCallQuestionMarkIcon';
import { ToolCallReadDocumentIcon } from 'src/components/icons/ToolCallReadDocumentIcon';
import { ToolCallResearchIcon } from 'src/components/icons/ToolCallResearchIcon';
import { ToolCallSearchIcon } from 'src/components/icons/ToolCallSearchIcon';
import { ToolCallSwapAgentIcon } from 'src/components/icons/ToolCallSwapAgentIcon';
import { WarningIcon } from 'src/components/icons/WarningIcon';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { getTypographyForSx, important } from 'src/theme';
import { getIsPlainRecord } from 'src/utils/get-is-plain-record';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

import { getWorkflowResultCandidates, WorkflowResultCandidateList } from './CodeWorkflowElement';
import { getIsTerminalAssistantStatus } from '../../../chat-hooks/harness-visible-work';
import { getIsPinnedSkillCardToolCallId } from '../../../ChatPanel/pinned-skill-card-id';
import { getSkillLoadVersionIds } from '../../../ChatPanel/skill-load-version-ids';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { useChatAssistantMessage } from '../../ChatPanelMarkdown';
import { parseCodeBlockJson } from '../../helpers/parseCodeBlockJson';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';
import { LinkChip } from '../AnchorElement/AnchorElement';

const toolCallIconMap: Record<ToolCallType, React.ComponentType<SimpleIconInterface>> = {
  askUser: ToolCallQuestionMarkIcon,
  assignTask: ToolCallWorkflowIcon,
  calculator: ToolCallCalculatorIcon,
  commentOnTask: ToolCallCommentOnTaskIcon,
  compactPriorOutput: ToolCallCompactPriorOutputIcon,
  completeTask: ToolCallCompleteTaskIcon,
  createReport: ToolCallReportIcon,
  createSkill: ToolCallSkillIcon,
  // Shares the reaching-outside-Aline icon with web page fetches.
  externalConnect: ToolCallGetWebpagesIcon,
  externalTool: ToolCallGetWebpagesIcon,
  generatePlaybook: ToolCallPlaybookIcon,
  getTask: ToolCallGetTaskIcon,
  getTaskAgentThread: ToolCallGetTaskIcon,
  getWebPageContents: ToolCallGetWebpagesIcon,
  listTasks: ToolCallWorkflowIcon,
  loadSkill: ToolCallSkillIcon,
  managePhases: ToolCallWorkflowIcon,
  modelFallback: ToolCallModelFallbackIcon,
  queryDocument: ToolCallQueryDocumentIcon,
  readDocument: ToolCallReadDocumentIcon,
  readIntakeForm: ToolCallReadDocumentIcon,
  readPlaybook: ToolCallReadDocumentIcon,
  readSkill: ToolCallSkillIcon,
  readSkillResource: ToolCallReadDocumentIcon,
  readWorkflow: ToolCallWorkflowIcon,
  reassignTask: ToolCallWorkflowIcon,
  research: ToolCallResearchIcon,
  runPlaybook: ToolCallPlaybookIcon,
  search: ToolCallSearchIcon,
  searchPlaybooks: ToolCallSearchIcon,
  searchSkillResources: ToolCallSearchIcon,
  searchSkills: ToolCallSearchIcon,
  selectDocuments: ToolCallReadDocumentIcon,
  semanticSearch: ToolCallSearchIcon,
  swapAgent: ToolCallSwapAgentIcon,
  updateIntakeForm: ToolCallWorkflowIcon,
  updateSkill: ToolCallSkillIcon,
  updateTaskDescription: ToolCallWorkflowIcon,
  workflowCollectIntakeForm: ToolCallWorkflowIcon,
  workflowCollectLaunchInfo: ToolCallWorkflowIcon,
  workflowCreateDraft: ToolCallWorkflowIcon,
  workflowLaunchTemplate: ToolCallWorkflowIcon,
  workflowSearchExisting: ToolCallWorkflowIcon,
  workflowSearchTemplates: ToolCallWorkflowIcon,
};

export const toolIconProps: SimpleIconInterface = {
  fill: '#ffffff',
  height: 14,
  stroke: '#c0c0c0',
  width: 14,
};

function ToolCallIcon({ type }: { type: ToolCallType }) {
  const Icon = toolCallIconMap[type];
  return <Icon {...toolIconProps} />;
}

function ToolCallCompactPriorOutputIcon(props: SimpleIconInterface) {
  return <ArchiveRestore {...props} />;
}

function ToolCallCommentOnTaskIcon(props: SimpleIconInterface) {
  return <MessageCircle {...props} />;
}

function ToolCallCompleteTaskIcon(props: SimpleIconInterface) {
  return <CheckCircle {...props} />;
}

function ToolCallGetTaskIcon(props: SimpleIconInterface) {
  return <Search {...props} />;
}

/** The same icon playbooks carry in the left nav, the tab bar and mentions. */
function ToolCallPlaybookIcon(props: SimpleIconInterface) {
  return <BookOpenText {...props} />;
}

/** The same icon skills carry in the admin list and the `/` picker. */
function ToolCallSkillIcon(props: SimpleIconInterface) {
  return <Sparkles {...props} />;
}

/** The same icon reports carry in the left nav. */
function ToolCallReportIcon(props: SimpleIconInterface) {
  return <ChartColumnBig {...props} />;
}

function ToolCallWorkflowIcon(props: SimpleIconInterface) {
  return <Workflow {...props} />;
}

const shimmer = keyframes`
  0% { background-position: -100% -100%; }
  100% { background-position: 100% 100%; }
`;

export const useToolCallStyles = makeStyles()(theme => ({
  clickableTitleContainer: {
    cursor: 'pointer',
  },
  description: {
    ...getTypographyForSx(theme.typography.body1),
    fontSize: 12,
    fontWeight: 400,
    marginTop: theme.spacing(0.25),
    wordBreak: 'break-word',
  },
  iconDiv: {
    alignItems: 'center',
    display: 'flex',
    flex: 0,
    justifyContent: 'center',
    maxWidth: 16,
    minWidth: 16,
  },
  inlineWorkflowResultSummary: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.secondaryType,
    fontSize: theme.typography.pxToRem(12),
  },
  inlineWorkflowResultWrapper: {
    marginTop: theme.spacing(1),
  },
  processingText: {
    // Apply shimmer to element and all deeply nested descendants
    '&, & *': {
      WebkitBackgroundClip: important('text'),
      animation: important(`${shimmer} 1s infinite linear`),
      background: important('linear-gradient(45deg, #999 25%, #666 50%, #999 75%)'),
      backgroundClip: important('text'),
      backgroundSize: important('200% 200%'),
      color: important('transparent'),
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    margin: theme.spacing(1, 0),
  },
  row: {
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  textDiv: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 14,
    fontWeight: 500,
  },
  titleContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  verticalLine: {
    backgroundColor: theme.appColors.rule,
    height: '100%',
    left: -0.5,
    position: 'relative',
    width: 1,
  },
}));

export const CodeToolCallElement = React.memo(function ToolCallElement({
  node,
}: MarkdownComponentProps): JSX.Element | null {
  const { classes } = useToolCallStyles();
  const message = useChatAssistantMessage();
  const codeBlockInfo = getCodeBlockInfoFromNode(node);
  const mentionEntityContext = useContext(MentionEntityContext);
  if (!codeBlockInfo) return null;

  const parsed = parseCodeBlockJson(codeBlockInfo.code, toolCallSchema, 'CodeToolCallElement');
  const toolCall = parsed.ok ? parsed.data : null;
  // Reading and searching a skill's files are steps inside the skill, not results to show.
  if (toolCall?.type === 'readSkillResource' || toolCall?.type === 'searchSkillResources')
    return null;
  if (
    toolCall?.type === 'loadSkill' &&
    getIsLoadShownByPinnedCard({
      loadedVersionIds: getSkillLoadVersionIds(message.data.uiParts ?? []),
      toolCall,
    })
  )
    return null;
  const toolApproval = toolCall
    ? getToolApprovalForToolCall(message.data.uiParts, toolCall.id)
    : null;
  const toolOutput = toolCall ? getToolOutputForToolCall(message.data.uiParts, toolCall.id) : null;
  const toolInput = toolOutput?.part.input ?? toolApproval?.part.input;
  const reportPresentation =
    toolCall?.type === 'createReport'
      ? getReportCreationPresentation({
          running: !getIsTerminalAssistantStatus(message.status),
          toolCall,
          uiParts: message.data.uiParts,
        })
      : null;
  /**
   * Client-input and needsApproval tools can leave the agent-message tool status
   * finished (or unset) while still waiting on the user. Treat pending client
   * input and unresolved approvals as processing so titles stay in-progress.
   */
  const isProcessing =
    reportPresentation?.processing ??
    Boolean(
      (toolCall && getIsAgentMessageToolCallProcessing(message.data.toolCalls, toolCall.id)) ||
      (toolCall && getIsClientInputToolAwaitingUser(toolCall.type, toolOutput?.output)) ||
      (toolCall && getPendingToolApprovalForToolCall(message.data.uiParts, toolCall.id))
    );
  const toolInfo =
    reportPresentation ??
    getToolInfo(
      toolCall,
      isProcessing,
      mentionEntityContext,
      toolApproval?.approved,
      toolInput,
      toolCall?.type === 'createSkill' || toolCall?.type === 'updateSkill'
        ? getSkillAuthoringToolOutput(message.data.uiParts, toolCall.id)
        : toolOutput?.output,
      toolApproval?.reason
    );
  const workflowResult = getWorkflowResult(toolOutput?.output);
  const inlineWorkflowSearchResult = getInlineWorkflowSearchResult({
    toolCall,
  });
  const workflowLaunchDetails =
    toolCall?.type === 'workflowLaunchTemplate'
      ? {
          approved: toolApproval?.approved,
          result: workflowResult,
          templateName: getWorkflowLabel(toolCall.templateWorkflowId, mentionEntityContext),
          templateWorkflowId: toolCall.templateWorkflowId,
          workflowName:
            workflowResult?.status === 'launched'
              ? getWorkflowLabel(workflowResult.workflow.workflowId, mentionEntityContext)
              : toolCall.workflowName,
        }
      : null;
  const loadSkillInfo =
    toolCall?.type === 'loadSkill'
      ? getLoadSkillToolInfo({
          loading: isProcessing,
          skillVersion: mentionEntityContext.skillVersions.get(toolCall.versionId) ?? null,
          toolCall,
        })
      : null;
  const shownInfo = loadSkillInfo ?? toolInfo;
  const showProcessing = isProcessing || Boolean(loadSkillInfo?.waiting);

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <div className={classes.iconDiv}>
          <ToolCallIconOrSpinner loading={showProcessing} type={toolCall?.type ?? null} />
        </div>
        <div className={classes.titleContainer}>
          <Typography
            className={clsx(classes.title, showProcessing && classes.processingText)}
            component="span"
          >
            {shownInfo.title}
          </Typography>
        </div>
      </div>
      <div className={classes.row}>
        <div className={classes.iconDiv}>
          <div className={classes.verticalLine} />
        </div>
        <div className={classes.textDiv}>
          <div className={clsx(classes.description, showProcessing && classes.processingText)}>
            <ToolCallDescription
              description={shownInfo.description}
              workflowLaunchDetails={workflowLaunchDetails}
            />
          </div>
          {toolCall?.type === 'askUser' && <AskUserInlineSummary output={toolOutput?.output} />}
          <InlineWorkflowSearchResult result={inlineWorkflowSearchResult} />
        </div>
      </div>
    </div>
  );
});

function InlineWorkflowSearchResult({ result }: { result: WorkflowAiToolCallResult | null }) {
  const { classes } = useToolCallStyles();
  if (!result) return null;
  if (result.status === 'errored') {
    return (
      <Typography className={classes.inlineWorkflowResultSummary} color="error">
        {result.errorMessage}
      </Typography>
    );
  }

  const candidates = getWorkflowResultCandidates(result);
  if (!candidates) return null;

  return (
    <div className={classes.inlineWorkflowResultWrapper}>
      <WorkflowResultCandidateList
        candidates={candidates}
        defaultExpanded={false}
        result={result}
        title={getWorkflowSearchResultTitle(candidates.length)}
      />
    </div>
  );
}

function ToolCallDescription({
  description,
  workflowLaunchDetails,
}: {
  description: React.ReactNode;
  workflowLaunchDetails: WorkflowLaunchDetails | null;
}) {
  if (workflowLaunchDetails) {
    return <WorkflowLaunchDescription details={workflowLaunchDetails} fallback={description} />;
  }

  return description;
}

interface WorkflowLaunchDetails {
  approved: boolean | undefined;
  result: WorkflowAiResult | null;
  templateName: string;
  templateWorkflowId: string;
  workflowName: string;
}

function WorkflowLaunchDescription({
  details,
  fallback,
}: {
  details: WorkflowLaunchDetails;
  fallback: React.ReactNode;
}) {
  const { approved, result, templateName, templateWorkflowId, workflowName } = details;
  if (result?.status === 'errored') return result.errorMessage;
  if (approved === false) return 'Workflow launch denied.';
  if (approved !== true && result?.status !== 'launched') return fallback;

  const launchedWorkflowLink =
    result?.status === 'launched'
      ? link.WorkflowDetailTabSummary({
          workflowId: result.workflow.workflowId,
        })
      : null;
  const templateUrl = link.TemplateSelectedTemplateSummary({
    workflowId: templateWorkflowId,
  });

  return (
    <>
      Launched &quot;
      {launchedWorkflowLink ? (
        <WorkflowEntityLink title={workflowName} url={launchedWorkflowLink} />
      ) : (
        workflowName
      )}
      &quot; from template &quot;
      <WorkflowEntityLink title={templateName} url={templateUrl} />
      &quot;
    </>
  );
}

function WorkflowEntityLink({ title, url }: { title: string; url: string }) {
  return <EntityLink title={title} url={url} />;
}

function TaskEntityLink({ title, url }: { title: string; url: string | null }) {
  if (!url) return title;
  return <EntityLink title={title} url={url} />;
}

function EntityLink({ title, url }: { title: string; url: string }) {
  return (
    <MuiLink
      href={url}
      onClick={event => {
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        openLink(url, { title });
      }}
    >
      {title}
    </MuiLink>
  );
}

function WorkflowCreateDraftDescription({
  mentionEntityContext,
  toolCall,
}: {
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>;
  toolCall: Extract<ToolCallSafeParseResult, { type: 'workflowCreateDraft' }>;
}) {
  const createdWorkflowId =
    toolCall.result?.status === 'created_draft' ? toolCall.result.workflow.workflowId : null;
  const workflowLabel = createdWorkflowId
    ? getWorkflowLabel(createdWorkflowId, mentionEntityContext, toolCall.workflowName)
    : toolCall.workflowName;

  return (
    <>
      &quot;
      {createdWorkflowId ? (
        <WorkflowEntityLink
          title={workflowLabel}
          url={link.WorkflowDetailTabSummary({ workflowId: createdWorkflowId })}
        />
      ) : (
        workflowLabel
      )}
      &quot;.
    </>
  );
}

function WorkflowSearchDescription({ retrievalQueries }: { retrievalQueries: string[] }) {
  const normalizedRetrievalQueries = retrievalQueries
    .map(retrievalQuery => retrievalQuery.trim())
    .filter(Boolean);
  return <>Query: {normalizedRetrievalQueries.join('; ')}.</>;
}

function getSemanticSearchDisplayQueries(
  toolCall: Extract<ToolCallSafeParseResult, { type: 'semanticSearch' }>
) {
  if (toolCall.retrievalQueries?.length) return toolCall.retrievalQueries;
  if (toolCall.query) return [toolCall.query];
  return [];
}

function getQueryDocumentDisplayQueries(
  toolCall: Extract<ToolCallSafeParseResult, { type: 'queryDocument' }>
) {
  if (toolCall.retrievalQueries?.length) return toolCall.retrievalQueries;
  if (toolCall.question) return [toolCall.question];
  return [];
}

function getWorkflowResult(output: unknown) {
  const parsed = WorkflowAiResultSchema.safeParse(output);
  return parsed.success ? parsed.data : null;
}

function getWorkflowToolCallResult(output: unknown) {
  const parsed = WorkflowAiToolCallResultSchema.safeParse(output);
  return parsed.success ? parsed.data : null;
}

function getInlineWorkflowSearchResult({ toolCall }: { toolCall: ToolCallSafeParseResult | null }) {
  if (!toolCall) return null;
  if (toolCall.type !== 'workflowSearchExisting' && toolCall.type !== 'workflowSearchTemplates')
    return null;

  return getWorkflowToolCallResult(toolCall.result);
}

function getWorkflowSearchResultTitle(count: number) {
  return `Found ${count} search ${count === 1 ? 'result' : 'results'}`;
}

function ToolCallIconOrSpinner({ loading, type }: { loading: boolean; type: ToolCallType | null }) {
  if (loading) return <CircularProgress size={14} />;
  if (!type) return <ToolCallQuestionMarkIcon {...toolIconProps} />;

  return <ToolCallIcon type={type} />;
}

const useWebPageContentURLsStyles = makeStyles()(() => ({
  root: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
}));

function WebPageContentURLs({ loading, urls }: { loading: boolean; urls: string[] }): JSX.Element {
  const { classes } = useWebPageContentURLsStyles();
  const text = loading
    ? 'Retrieving web page contents for URL(s):'
    : 'Retrieved web page contents for URL(s):';

  return (
    <div className={classes.root}>
      {text}
      {urls.map((url, index) => (
        <LinkChip key={index} href={url} />
      ))}
    </div>
  );
}

const useSwapAgentTitleStyles = makeStyles()(() => ({
  root: {
    alignItems: 'center',
    display: 'flex',
    gap: 4,
  },
}));

function SwapAgentTitle({
  loading,
  toolCall,
}: {
  loading: boolean;
  toolCall: SwapAgentToolCall;
}): JSX.Element {
  const { classes } = useSwapAgentTitleStyles();
  const toAgent = getAgentInfo(toolCall.toAgent).displayName;
  const fromAgent = getAgentInfo(toolCall.fromAgent).displayName;
  const baseTitle = loading
    ? `Transferring from ${fromAgent} to ${toAgent}...`
    : `Transferred from ${fromAgent} to ${toAgent}`;
  const originalModelNameText = toolCall.originalModel
    ? `Model ${getAiModelDisplayName(toolCall.originalModel)} failed`
    : 'Model failed';

  if (!toolCall.fallbackModel) return <Typography component="span">{baseTitle}</Typography>;

  return (
    <span className={classes.root}>
      {baseTitle}
      <Spacer />
      <AlineTooltip
        arrow
        placement="top"
        title={`${originalModelNameText}, falling back to ${getAiModelDisplayName(toolCall.fallbackModel)}. Response quality may be different than normal.`}
      >
        <WarningIcon style={{ height: toolIconProps.height, width: toolIconProps.width }} />
      </AlineTooltip>
    </span>
  );
}

/**
 * How an answered connect pause went, read from the tool output the card wrote.
 *
 * Anything unreadable returns null rather than guessing: reporting "connected"
 * for a pause the user actually declined would be worse than saying nothing.
 */
function getExternalConnectStatus(toolOutput: unknown): 'connected' | 'dismissed' | null {
  if (!getIsPlainRecord(toolOutput)) return null;
  if (toolOutput.status === 'connected') return 'connected';
  if (toolOutput.status === 'dismissed') return 'dismissed';
  return null;
}

type ToolCallSafeParseResult = NonNullable<ReturnType<typeof toolCallSchema.safeParse>['data']>;

/**
 * Every finished load has a card above the answer. Suppress its inline call while allowing
 * the synthetic card itself to render.
 */
export function getIsLoadShownByPinnedCard({
  loadedVersionIds,
  toolCall,
}: {
  loadedVersionIds: ReadonlySet<string>;
  toolCall: { id: string; versionId: string };
}) {
  if (getIsPinnedSkillCardToolCallId(toolCall.id)) return false;
  return loadedVersionIds.has(toolCall.versionId);
}

/**
 * A finished load shows its name only once the server confirms the user can read the version:
 * the name in the tool call may belong to a skill the user can no longer see. Until the
 * mention query answers, the row keeps waiting instead of saying unavailable.
 */
export function getLoadSkillToolInfo({
  loading,
  skillVersion,
  toolCall,
}: {
  loading: boolean;
  skillVersion: SkillVersionMentionEntity | null;
  toolCall: Extract<ToolCallSafeParseResult, { type: 'loadSkill' }>;
}) {
  if (!loading && !skillVersion)
    return {
      description: 'Checking that you can use this skill.',
      title: 'Loading skill...',
      waiting: true,
    };
  const version = skillVersion?.__typename === 'AgentSkillVersion' ? skillVersion : null;
  if (!loading && !version)
    return {
      description: 'It may have been deleted, or you no longer have access to it.',
      title: 'Skill unavailable',
      waiting: false,
    };

  const name = toolCall.name || version?.name || 'skill';
  return {
    description: (
      <>
        Skill:{' '}
        <AlineTooltip arrow placement="top" title={version?.description || name}>
          <span>
            <EntityLink
              title={name}
              url={link.AdminAgentSkillDetail({ skillId: toolCall.skillId })}
            />
          </span>
        </AlineTooltip>
      </>
    ),
    title: getLoadSkillTitle({ failed: Boolean(toolCall.failed), loading }),
    waiting: false,
  };
}

function getLoadSkillTitle({ failed, loading }: { failed: boolean; loading: boolean }) {
  if (loading) return 'Loading skill...';
  return failed ? 'Could not load skill' : 'Loaded skill';
}

/**
 * The original proposal shows its persisted outcome; a successful save links to the editor.
 */
function getSkillProposalToolInfo({
  approvalReason,
  approved,
  loading,
  output,
  toolCall,
}: {
  approved: boolean | undefined;
  loading: boolean;
  output: unknown;
  toolCall: Extract<ToolCallSafeParseResult, { type: 'createSkill' | 'updateSkill' }>;
  approvalReason?: string;
}) {
  const presentation = getSkillAuthoringPresentation({
    approvalReason,
    approved,
    loading,
    output,
    toolCall,
  });
  return {
    description: presentation.skill ? (
      <EntityLink
        title={presentation.skill.name}
        url={link.AdminAgentSkillDetail({ skillId: presentation.skill.id })}
      />
    ) : (
      presentation.description
    ),
    title: presentation.title,
    type: toolCall.type,
  };
}

function getToolInfo(
  toolCall: ToolCallSafeParseResult | null,
  loading: boolean,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>,
  approved?: boolean,
  toolInput?: unknown,
  toolOutput?: unknown,
  approvalReason?: string
): {
  description: React.ReactNode;
  title: React.ReactNode;
  type: ToolCallType | 'unknown-tool';
} {
  if (!toolCall) {
    return {
      description: 'An unknown tool call was detected.',
      title: loading ? 'Processing tool call...' : 'Processed tool call',
      type: 'unknown-tool',
    };
  }

  switch (toolCall.type) {
    case 'askUser':
      if (toolCall.preparing)
        return {
          description: 'Working out what it needs from you.',
          title: 'Preparing questions...',
          type: toolCall.type,
        };
      return {
        description: loading ? 'Waiting for your answer.' : 'Answer received.',
        title: loading ? 'Asking for input...' : 'User input provided',
        type: toolCall.type,
      };
    case 'calculator':
      return {
        description: loading
          ? `Computing expression: ${toolCall.expression}.`
          : `Computed expression: ${toolCall.expression}.`,
        title: loading ? 'Calculating...' : 'Calculated',
        type: toolCall.type,
      };
    case 'commentOnTask':
      return {
        description: loading ? 'Adding a task comment.' : 'Added a task comment.',
        title: loading ? 'Adding comment...' : 'Added comment',
        type: toolCall.type,
      };
    case 'compactPriorOutput':
      return {
        description: `Prior output from ${toolCall.sourceAgent} exceeded the context budget (${toolCall.originalTokens} tokens). Target compacted size: ${toolCall.targetTokens} tokens.`,
        title: loading ? 'Compacting prior output...' : 'Compacted prior output',
        type: toolCall.type,
      };
    case 'completeTask':
      return {
        description: loading ? 'Marking the task complete.' : 'Marked the task complete.',
        title: loading ? 'Completing task...' : 'Completed task',
        type: toolCall.type,
      };
    case 'queryDocument': {
      const queryDocumentLabel = getDocumentLabel(toolCall.documentId, mentionEntityContext);
      if (loading && toolCall.waitingForExtraction) {
        return {
          description: `Awaiting document processing for "${queryDocumentLabel}"`,
          title: 'Awaiting document processing',
          type: toolCall.type,
        };
      }
      return {
        description: (
          <WorkflowSearchDescription retrievalQueries={getQueryDocumentDisplayQueries(toolCall)} />
        ),
        title: loading
          ? `Searching document ${queryDocumentLabel}...`
          : `Searched document ${queryDocumentLabel}`,
        type: toolCall.type,
      };
    }
    case 'readDocument': {
      const readDocumentLabel = getDocumentLabel(toolCall.documentId, mentionEntityContext);
      if (loading && toolCall.waitingForExtraction) {
        return {
          description: `Awaiting document processing for "${readDocumentLabel}"`,
          title: 'Awaiting document processing',
          type: toolCall.type,
        };
      }
      return {
        description: loading
          ? `Reading document: ${readDocumentLabel}.`
          : `Read document: ${readDocumentLabel}.`,
        title: loading ? 'Reading document...' : 'Read document',
        type: toolCall.type,
      };
    }
    case 'getTask':
      return {
        description: loading ? 'Reading the task details.' : 'Read the task details.',
        title: loading ? 'Reading task...' : 'Read task',
        type: toolCall.type,
      };
    case 'getTaskAgentThread':
      return {
        description: loading ? 'Reading the task agent thread.' : 'Read the task agent thread.',
        title: loading ? 'Reading task thread...' : 'Read task thread',
        type: toolCall.type,
      };
    case 'listTasks':
      return {
        description: loading ? "Listing the workflow's tasks." : "Listed the workflow's tasks.",
        title: loading ? 'Listing tasks...' : 'Listed tasks',
        type: toolCall.type,
      };
    case 'readIntakeForm': {
      const workflowLabel = getWorkflowLabel(toolCall.workflowId, mentionEntityContext);
      const workflowUrl = link.WorkflowDetailTabSummary({
        workflowId: toolCall.workflowId,
      });
      return {
        description: (
          <>
            {loading ? 'Reading' : 'Read'} intake form for &quot;
            <WorkflowEntityLink title={workflowLabel} url={workflowUrl} />
            &quot;.
          </>
        ),
        title: loading ? 'Reading intake form...' : 'Read intake form',
        type: toolCall.type,
      };
    }
    case 'updateIntakeForm':
      return {
        description: loading ? 'Filling in the intake form.' : 'Filled in the intake form.',
        title: loading ? 'Filling intake form...' : 'Filled intake form',
        type: toolCall.type,
      };
    case 'reassignTask':
      return {
        description: loading
          ? "Updating the task's assignees or reviewers."
          : "Updated the task's assignees or reviewers.",
        title: loading ? 'Updating task assignments...' : 'Updated task assignments',
        type: toolCall.type,
      };
    case 'research':
      return {
        description: `Research plan: ${toolCall.instructions}`,
        title: loading ? 'Researching the Internet...' : 'Researched the Internet',
        type: toolCall.type,
      };
    case 'getWebPageContents':
      return {
        description: <WebPageContentURLs loading={loading} urls={toolCall.urls} />,
        title: loading
          ? `Reading web page contents for ${toolCall.urls.length} URL${toolCall.urls.length === 1 ? '' : 's'}...`
          : `Read web page contents for ${toolCall.urls.length} URL${toolCall.urls.length === 1 ? '' : 's'}`,
        type: toolCall.type,
      };
    case 'search':
      return {
        description: loading
          ? `Searching the Internet for: ${toolCall.query}.`
          : `Searched the Internet for: ${toolCall.query}.`,
        title: loading ? 'Searching the Internet...' : 'Searched the Internet',
        type: toolCall.type,
      };
    case 'searchPlaybooks':
      return {
        description: loading
          ? `Searching playbooks for: ${toolCall.query}.`
          : `Searched playbooks for: ${toolCall.query}.`,
        title: loading ? 'Searching playbooks...' : 'Searched playbooks',
        type: toolCall.type,
      };
    case 'searchSkills':
      return {
        description: loading
          ? `Searching your skills for: ${toolCall.query}.`
          : `Searched your skills for: ${toolCall.query}.`,
        title: loading ? 'Searching skills...' : 'Searched skills',
        type: toolCall.type,
      };
    case 'readSkill':
      return {
        description: 'Read the skill to change.',
        title: loading ? 'Reading skill...' : 'Read skill',
        type: toolCall.type,
      };
    case 'createSkill':
    case 'updateSkill':
      return getSkillProposalToolInfo({
        approvalReason,
        approved,
        loading,
        output: toolOutput,
        toolCall,
      });
    case 'loadSkill':
      return {
        description: 'Loaded a skill for this task.',
        title: loading ? 'Loading skill...' : 'Loaded skill',
        type: toolCall.type,
      };
    case 'readPlaybook':
      return {
        description: `Playbook: ${getPlaybookLabel(toolCall.playbookId, mentionEntityContext)}.`,
        title: loading ? 'Reading playbook...' : 'Read playbook',
        type: toolCall.type,
      };
    case 'readSkillResource':
      return {
        description: `Skill resource: ${toolCall.path}.`,
        title: loading ? 'Reading skill resource...' : 'Read skill resource',
        type: toolCall.type,
      };
    case 'searchSkillResources':
      return {
        description: `Search: ${toolCall.query}.`,
        title: loading ? 'Searching skill files...' : 'Searched skill files',
        type: toolCall.type,
      };
    case 'createReport':
      return {
        ...getReportCreationPresentation({ running: loading, toolCall }),
        type: toolCall.type,
      };
    case 'generatePlaybook': {
      if (toolCall.preparing) {
        return {
          description: 'Working out the categories and rules for your review.',
          title: 'Preparing playbook plan...',
          type: toolCall.type,
        };
      }
      const documentIds = toolCall.documentIds ?? [];
      const documentLine = documentIds.length ? (
        <div>
          {documentIds.length === 1 ? 'Document' : 'Documents'}:{' '}
          {documentIds
            .map(documentId => getDocumentLabel(documentId, mentionEntityContext))
            .join(', ')}
        </div>
      ) : null;
      if (approved === false) {
        return {
          description: documentLine,
          title: 'Playbook plan skipped',
          type: toolCall.type,
        };
      }
      /**
       * Between the plan being put up and the user answering, the tool has not
       * run: saying a generation started would be wrong, and the card is the
       * thing they are being asked to decide on.
       */
      if (!toolCall.generationJobId) {
        return {
          description: documentLine,
          title: 'Waiting on plan approval',
          type: toolCall.type,
        };
      }
      return {
        description: documentLine,
        title: loading ? 'Generating playbook...' : 'Started playbook generation',
        type: toolCall.type,
      };
    }
    case 'runPlaybook': {
      const playbookLabel = getPlaybookLabel(toolCall.playbookId, mentionEntityContext);
      const documentLabel = getDocumentLabel(toolCall.documentId, mentionEntityContext);

      return {
        description: (
          <>
            <div>Playbook: {playbookLabel}</div>
            <div>Document: {documentLabel}</div>
          </>
        ),
        title: loading ? 'Running playbook...' : 'Started playbook run',
        type: toolCall.type,
      };
    }
    case 'semanticSearch':
      return {
        description: (
          <WorkflowSearchDescription retrievalQueries={getSemanticSearchDisplayQueries(toolCall)} />
        ),
        title: loading ? 'Searching repository...' : 'Searched repository',
        type: toolCall.type,
      };
    case 'selectDocuments':
      return {
        description: 'Selected repository documents for downstream agents.',
        title: loading ? 'Selecting documents...' : 'Selected documents',
        type: toolCall.type,
      };
    case 'swapAgent': {
      return {
        description: getAgentInfo(toolCall.toAgent).description,
        title: <SwapAgentTitle loading={loading} toolCall={toolCall} />,
        type: toolCall.type,
      };
    }
    case 'modelFallback': {
      const toModel = getAiModelDisplayName(toolCall.toModel);
      const fromModel = getAiModelDisplayName(toolCall.fromModel);

      return {
        description: `AI model ${fromModel} failed, falling back to ${toModel}.`,
        title: `Retrying request...`,
        type: toolCall.type,
      };
    }
    case 'updateTaskDescription':
      return {
        description: loading ? 'Updating the task description.' : 'Updated the task description.',
        title: loading ? 'Updating description...' : 'Updated description',
        type: toolCall.type,
      };
    case 'workflowCollectIntakeForm':
      if (getIsCollectIntakeFormSkipped(toolOutput)) {
        return {
          description: 'Skipped the workflow intake form.',
          title: 'Skipped workflow intake',
          type: toolCall.type,
        };
      }
      return {
        description: loading
          ? 'Waiting for the workflow intake form.'
          : 'Workflow intake form submitted.',
        title: loading ? 'Collecting workflow intake...' : 'Collected workflow intake',
        type: toolCall.type,
      };
    case 'workflowCollectLaunchInfo':
      if (getIsCollectIntakeFormSkipped(toolOutput)) {
        return {
          description: 'Skipped the workflow launch information.',
          title: 'Skipped workflow launch information',
          type: toolCall.type,
        };
      }
      return {
        description: loading
          ? 'Waiting for workflow launch information.'
          : 'Workflow launch information submitted.',
        title: loading ? 'Collecting launch information...' : 'Collected launch information',
        type: toolCall.type,
      };
    case 'workflowCreateDraft': {
      if (toolCall.preparing) {
        return {
          description: 'Building the workflow structure for your review.',
          title: 'Preparing workflow details...',
          type: toolCall.type,
        };
      }
      if (toolCall.result?.status === 'errored') {
        return {
          description: toolCall.result.errorMessage,
          title: 'Could not create draft workflow',
          type: toolCall.type,
        };
      }
      if (approved === false) {
        return {
          description: (
            <WorkflowCreateDraftDescription
              mentionEntityContext={mentionEntityContext}
              toolCall={toolCall}
            />
          ),
          title: 'Draft workflow creation denied',
          type: toolCall.type,
        };
      }
      if (loading || toolCall.result?.status !== 'created_draft') {
        return {
          description: (
            <WorkflowCreateDraftDescription
              mentionEntityContext={mentionEntityContext}
              toolCall={toolCall}
            />
          ),
          title: 'Creating draft workflow...',
          type: toolCall.type,
        };
      }
      return {
        description: (
          <WorkflowCreateDraftDescription
            mentionEntityContext={mentionEntityContext}
            toolCall={toolCall}
          />
        ),
        title: 'Created draft workflow',
        type: toolCall.type,
      };
    }
    case 'workflowLaunchTemplate':
      return {
        description: 'Review and approve this workflow launch.',
        title: loading ? 'Preparing workflow launch...' : 'Launch workflow',
        type: toolCall.type,
      };
    /**
     * The step in the timeline, not the thing to act on: the live card sits
     * above the composer while the turn is suspended, and this is what the
     * conversation still reads like once it has been answered.
     */
    /**
     * Reads as a wait while the user is away at the provider, the same way
     * `askUser` does: the turn is suspended on a person either way, and the
     * only honest thing to show is that it is still waiting on them.
     */
    case 'externalConnect': {
      const connectLabel = getExternalToolkitLabel(toolCall.toolkit);
      if (loading) {
        return {
          description: 'Waiting for you to finish connecting.',
          title: `Connecting ${connectLabel}...`,
          type: toolCall.type,
        };
      }
      // An answered pause says which way it went; an unreadable one claims neither.
      const connectStatus = getExternalConnectStatus(toolOutput);
      if (connectStatus === 'connected') {
        return {
          description: `${connectLabel} account connected.`,
          title: `Connected ${connectLabel}`,
          type: toolCall.type,
        };
      }
      if (connectStatus === 'dismissed') {
        return {
          description: `Continued without connecting ${connectLabel}.`,
          title: `Skipped connecting ${connectLabel}`,
          type: toolCall.type,
        };
      }
      return {
        description: getExternalConnectDescription(toolCall.toolkit),
        title: getExternalConnectTitle(toolCall.toolkit),
        type: toolCall.type,
      };
    }
    case 'externalTool':
      return {
        /**
         * Two different claims, so two lines. The criteria say what Aline asked
         * the vendor for; the outcome says what it could verify of the answer.
         * Collapsing them would leave a search that found nothing looking
         * identical to one that asked the wrong question.
         */
        description: (
          <>
            {toolCall.criteria ? <div>Search: {toolCall.criteria}</div> : null}
            <div>
              {toolCall.resolved
                ? getExternalToolResolutionLabel(toolCall.resolved)
                : `${getExternalToolkitLabel(toolCall.toolkit)}: ${toolCall.toolSlugs.join(', ')}.`}
            </div>
          </>
        ),
        title: getExternalToolCallTitle({
          toolSlugs: toolCall.toolSlugs,
          toolkit: toolCall.toolkit,
        }),
        type: toolCall.type,
      };
    case 'workflowSearchExisting':
      return {
        description: <WorkflowSearchDescription retrievalQueries={toolCall.retrievalQueries} />,
        title: loading ? 'Searching existing workflows...' : 'Searched existing workflows',
        type: toolCall.type,
      };
    case 'workflowSearchTemplates':
      return {
        description: <WorkflowSearchDescription retrievalQueries={toolCall.retrievalQueries} />,
        title: loading ? 'Searching workflow templates...' : 'Searched workflow templates',
        type: toolCall.type,
      };
    case 'readWorkflow': {
      const workflowId = toolCall.workflowId ?? getInputString(toolInput, 'workflowId');
      if (!workflowId) {
        return {
          description: loading
            ? 'Reading workflow structure and status.'
            : 'Read workflow structure and status.',
          title: loading ? 'Reading workflow...' : 'Read workflow',
          type: toolCall.type,
        };
      }
      const workflowLabel = getWorkflowLabel(workflowId, mentionEntityContext, 'workflow');
      const workflowUrl = link.WorkflowDetailTabSummary({ workflowId });
      return {
        description: (
          <>
            {loading ? 'Reading' : 'Read'} structure and status for &quot;
            <WorkflowEntityLink title={workflowLabel} url={workflowUrl} />
            &quot;.
          </>
        ),
        title: loading ? 'Reading workflow...' : 'Read workflow',
        type: toolCall.type,
      };
    }
    case 'managePhases':
      return {
        description: loading
          ? `Review and approve this phase change (${toolCall.operation}).`
          : `Applied a phase change (${toolCall.operation}).`,
        title: loading ? 'Preparing phase change...' : 'Manage phases',
        type: toolCall.type,
      };
    case 'assignTask': {
      const taskId = toolCall.taskId ?? getInputString(toolInput, 'taskId');
      const taskLink = taskId
        ? getTaskEntityLink(taskId, mentionEntityContext)
        : { title: 'this task', url: null };
      if (approved === false) {
        return {
          description: (
            <>
              Assignment denied for &quot;
              <TaskEntityLink title={taskLink.title} url={taskLink.url} />
              &quot;.
            </>
          ),
          title: 'Task assignment denied',
          type: toolCall.type,
        };
      }
      if (loading) {
        return {
          description: (
            <>
              Assign &quot;
              <TaskEntityLink title={taskLink.title} url={taskLink.url} />
              &quot;.
            </>
          ),
          title: 'Preparing task assignment...',
          type: toolCall.type,
        };
      }
      return {
        description: (
          <>
            Assigned &quot;
            <TaskEntityLink title={taskLink.title} url={taskLink.url} />
            &quot;.
          </>
        ),
        title: 'Assigned task',
        type: toolCall.type,
      };
    }
  }
}

function getInputString(input: unknown, key: string) {
  if (!getIsPlainRecord(input)) return null;
  const value = input[key];
  return typeof value === 'string' ? value : null;
}

export function getIsClientInputToolAwaitingUser(type: ToolCallType, output: unknown) {
  if (
    type !== 'askUser' &&
    type !== 'externalConnect' &&
    type !== 'workflowCollectIntakeForm' &&
    type !== 'workflowCollectLaunchInfo'
  )
    return false;
  if (output === undefined || output === null) return true;
  if (getIsPlainRecord(output) && output.status === 'awaiting_user_input') return true;
  if (type === 'askUser') return getAskUserAnswers(output) === null;
  /**
   * A connect pause is answered with connected or dismissed. Until one of those
   * arrives the user is still away at the provider, which is the same kind of
   * wait `askUser` is in, and the only thing that makes the step render as one.
   */
  if (type === 'externalConnect') return getExternalConnectStatus(output) === null;
  return !getHasCollectedLaunchValues(output) && !getIsCollectIntakeFormSkipped(output);
}

function getHasCollectedLaunchValues(output: unknown) {
  if (!getIsPlainRecord(output)) return false;
  return (
    getIsPlainRecord(output.intakeFormValues) ||
    getIsPlainRecord(output.alineFieldValues) ||
    Array.isArray(output.instanceAssignments)
  );
}

function getIsCollectIntakeFormSkipped(output: unknown) {
  return getIsPlainRecord(output) && output.skipped === true;
}

function getPlaybookLabel(
  playbookId: string,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>
) {
  const playbook = mentionEntityContext.playbooks.get(playbookId);
  if (playbook?.__typename === 'Playbook') return playbook.title;
  if (playbook?.__typename === 'MentionAccessError')
    return getAccessErrorLabel('playbook', playbook.reason);
  return 'Unavailable playbook';
}

function getWorkflowLabel(
  workflowId: string,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>,
  fallback = 'Selected template'
) {
  const workflow = mentionEntityContext.workflows.get(workflowId);
  if (workflow?.__typename === 'Workflow') return workflow.name || 'Untitled Template';
  return fallback;
}

function getTaskEntityLink(
  taskId: string,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>
) {
  const task = mentionEntityContext.tasks.get(taskId);
  switch (task?.__typename) {
    case 'Task':
      return {
        title: task.title || 'Untitled Task',
        url: link.WorkflowDetailTabTask({ taskId, workflowId: task.workflow.id }),
      };
    case 'MentionAccessError':
      return {
        title: getAccessErrorLabel('task', task.reason),
        url: null,
      };
    default:
      return { title: 'Task', url: null };
  }
}

function getDocumentLabel(
  documentId: string,
  mentionEntityContext: React.ContextType<typeof MentionEntityContext>
) {
  const document = mentionEntityContext.documents.get(documentId);
  if (document?.__typename === 'Document') return document.name;
  if (document?.__typename === 'MentionAccessError')
    return getAccessErrorLabel('document', document.reason);
  return 'Unavailable document';
}

function getAccessErrorLabel(
  entityLabel: 'document' | 'playbook' | 'task',
  reason: Apollo.MentionAccessErrorReason
) {
  if (reason === Apollo.MentionAccessErrorReason.Deleted) return `Deleted ${entityLabel}`;
  if (reason === Apollo.MentionAccessErrorReason.NoAccess) return `No access to ${entityLabel}`;

  return `Unavailable ${entityLabel}`;
}

/**
 * Convert modelName to user-friendly provider name
 */
export function getAiModelDisplayName(modelName: AiModel): string {
  switch (modelName) {
    case 'claude4Sonnet':
      return 'Claude 4 Sonnet';
    case 'claude37Sonnet':
      return 'Claude 3.7 Sonnet';
    case 'claude45Haiku':
      return 'Claude 4.5 Haiku';
    case 'claude45Sonnet':
      return 'Claude 4.5 Sonnet';
    case 'claude46Sonnet':
      return 'Claude 4.6 Sonnet';
    case 'claude55Sonnet':
      return 'Claude Sonnet 5.5';
    case 'claude5Sonnet':
      return 'Claude Sonnet 5';
    case 'claude5Fable':
      return 'Claude Fable 5';
    case 'claude55Opus':
      return 'Claude Opus 5.5';
    case 'claude5Opus':
      return 'Claude Opus 5';
    case 'claude46Opus':
      return 'Claude 4.6 Opus';
    case 'claude47Opus':
      return 'Claude 4.7 Opus';
    case 'claude48Opus':
      return 'Claude 4.8 Opus';
    case 'gemini25Flash':
      return 'Gemini 2.5 Flash';
    case 'gemini25FlashLite':
      return 'Gemini 2.5 Flash Lite';
    case 'gemini25Pro':
      return 'Gemini 2.5 Pro';
    case 'gemini3Pro':
      return 'Gemini 3 Pro';
    case 'gemini35Flash':
      return 'Gemini 3.5 Flash';
    case 'gemini35FlashLite':
      return 'Gemini 3.5 Flash Lite';
    case 'gemini36Flash':
      return 'Gemini 3.6 Flash';
    case 'gemini38Flash':
      return 'Gemini 3.8 Flash';
    case 'gemini37Flash':
      return 'Gemini 3.7 Flash';
    case 'gemini31FlashLite':
      return 'Gemini 3.1 Flash Lite';
    case 'openai4o':
      return 'OpenAI GPT-4o';
    case 'openai4omini':
      return 'OpenAI GPT-4o Mini';
    case 'openai41':
      return 'OpenAI GPT-4.1';
    case 'openai41Mini':
      return 'OpenAI GPT-4.1 Mini';
    case 'openaio1':
      return 'OpenAI o1';
    case 'openaio3':
      return 'OpenAI o3';
    case 'openaio3Mini':
      return 'OpenAI o3 Mini';
    case 'openai5':
      return 'OpenAI GPT-5';
    case 'openai5Mini':
      return 'OpenAI GPT-5 Mini';
    case 'openai51':
      return 'OpenAI GPT-5.1';
    case 'openai52':
      return 'OpenAI GPT-5.2';
    case 'openai54':
      return 'OpenAI GPT-5.4';
    case 'openai54Mini':
      return 'OpenAI GPT-5.4 Mini';
    case 'openai54Nano':
      return 'OpenAI GPT-5.4 Nano';
    case 'openai55':
      return 'OpenAI GPT-5.5';
    case 'openai61Sol':
      return 'OpenAI GPT-6.1 Sol';
    case 'openai6Sol':
      return 'OpenAI GPT-6 Sol';
    case 'openai6Luna':
      return 'OpenAI GPT-6 Luna';
    case 'openai6Astra':
      return 'OpenAI GPT-6 Astra';
    case 'openai56Sol':
      return 'OpenAI GPT-5.6 Sol';
    case 'openai56Terra':
      return 'OpenAI GPT-5.6 Terra';
    case 'openai56Luna':
      return 'OpenAI GPT-5.6 Luna';
    default:
      assertNeverSafely(modelName);
      return 'Unknown Model';
  }
}

type AgentInfo = { description: string; displayName: string };

/**
 * Maps agent enum values to user-facing labels. V1 agents (orchestrator,
 * documentAnalysis, etc.) are kept here so that historical AgentMessage
 * rows whose tool-call payloads carry these names still render — V1 itself
 * is gone but the rows persist.
 */
function getAgentInfo(agent: AgentName): AgentInfo {
  switch (agent) {
    case 'documentAnalysis':
      return {
        description:
          'The Document Analysis Agent analyzes documents to extract information and generate summaries.',
        displayName: 'Document Analysis Agent',
      };
    case 'draftDocument':
      return {
        description:
          'The Draft Document Agent drafts a document based on the provided instructions.',
        displayName: 'Draft Document Agent',
      };
    case 'draftDocumentLongContext':
      return {
        description:
          'The Draft Document (Long Context) Agent is used to draft a document based on the provided instructions and can handle longer context than the Draft Document Agent.',
        displayName: 'Draft Document (Long Context) Agent',
      };
    case 'editSelection':
      return {
        description:
          'The Edit Selection Agent suggests edits to a selection in the document based on the provided instructions.',
        displayName: 'Edit Selection Agent',
      };
    case 'editSelectionLongContext':
      return {
        description:
          'The Edit Selection (Long Context) Agent is used to suggest edits to a selection in the document based on the provided instructions and can handle longer context than the Edit Selection Agent.',
        displayName: 'Edit Selection (Long Context) Agent',
      };
    case 'generalLegal':
      return {
        description:
          'The General Legal Agent is a general legal agent that can help with a wide range of legal questions.',
        displayName: 'General Legal Agent',
      };
    case 'orchestrator':
      return {
        description:
          'The Orchestrator Agent orchestrates the conversation and ensures all agents work together.',
        displayName: 'Orchestrator Agent',
      };
    case 'redline':
      return {
        description:
          'The Redline Agent reviews documents and produces redlined suggestions for improvement.',
        displayName: 'Redline Agent',
      };
    case 'redlineLongContext':
      return {
        description:
          'The Redline (Long Context) Agent reviews documents and produces redlined suggestions for improvement and can handle longer context than the Redline Agent.',
        displayName: 'Redline (Long Context) Agent',
      };
    case 'suggestEdits':
      return {
        description:
          'The Suggest Edits Agent suggests edits to the document based on the provided instructions.',
        displayName: 'Suggest Edits Agent',
      };
    case 'suggestEditsLongContext':
      return {
        description:
          'The Suggest Edits (Long Context) Agent is used to suggest edits to the document based on the provided instructions and can handle longer context than the Suggest Edits Agent.',
        displayName: 'Suggest Edits (Long Context) Agent',
      };
    case 'workflow':
      return {
        description:
          'The Workflow Agent searches for existing workflows or templates, launches workflows from templates, and creates draft workflows.',
        displayName: 'Workflow Agent',
      };
    default:
      assertNeverSafely(agent);
      return { description: 'Unknown Agent', displayName: 'Unknown Agent' };
  }
}
